import { ExpandMore as ExpandMoreIcon } from 'components/icons';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { FormattedDate } from 'components/misc/FormattedDate';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FeatureFlag } from 'types/features';
import { InvoiceData, InvoiceMatterDetailsDto } from 'types/invoices';
import { AuditDates } from 'types/other';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { pathForResult } from 'utils/globalSearchRoutes';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { SECTION_HEADER, SECTION_PANEL } from '../details-tab/SectionCard';

interface Props {
    invoice: InvoiceData;
}

export type BillingInfoType = 'billing' | 'invoicing';

/** Where a note is kept: on the payer, on a case's client, on a case, or on a case's service agreement. */
export type BillingInfoSource = 'payer' | 'client' | 'case' | 'serviceAgreement';

export interface BillingInfoEntry {
    key: string;
    source: BillingInfoSource;
    content: string | null;
    auditDates: AuditDates;
    /** The case the note is on, or whose client or service agreement holds it; none for the payer's. */
    invoiceMatter?: InvoiceMatterDetailsDto;
    hasAdminFeeAllowed?: boolean;
    adminFeePercentage?: number;
}

/**
 * The invoice's billing notes or invoicing info, the latest first within each source: the cases', the service
 * agreements' (billing notes also list a service agreement that only allows an admin fee), the payer's, then the
 * billing notes of a case's client other than the payer.
 */
export function getBillingInfoEntries(invoice: InvoiceData, type: BillingInfoType): BillingInfoEntry[] {
    const infoKey = type === 'billing' ? 'billingNotes' : 'invoicingInfo';
    const matters = invoice.invoiceMatters ?? [];
    return [
        ...matters
            .filter((invoiceMatter) => invoiceMatter.matterBillingDetails[infoKey])
            .sort(
                (a, b) =>
                    Date.parse(b.matterBillingDetails.lastModifiedAt ?? '0') -
                    Date.parse(a.matterBillingDetails.lastModifiedAt ?? '0')
            )
            .map(
                (invoiceMatter): BillingInfoEntry => ({
                    key: `${invoiceMatter.matterId}-matterBillingDetails`,
                    source: 'case',
                    content: invoiceMatter.matterBillingDetails[infoKey],
                    auditDates: {
                        createdAt: invoiceMatter.matterBillingDetails.createdAt,
                        lastModifiedAt: invoiceMatter.matterBillingDetails.lastModifiedAt,
                    },
                    invoiceMatter,
                })
            ),
        ...matters
            .filter(
                (invoiceMatter) =>
                    invoiceMatter.serviceAgreementBillingDetails[infoKey] ||
                    (type === 'billing' &&
                        (invoiceMatter.hasAdminFeeAllowed || invoiceMatter.adminFeePercentage != null))
            )
            .sort(
                (a, b) =>
                    Date.parse(b.serviceAgreementBillingDetails.lastModifiedAt ?? '0') -
                    Date.parse(a.serviceAgreementBillingDetails.lastModifiedAt ?? '0')
            )
            .map(
                (invoiceMatter): BillingInfoEntry => ({
                    key: `${invoiceMatter.matterId}-serviceAgreementBillingDetails`,
                    source: 'serviceAgreement',
                    content: invoiceMatter.serviceAgreementBillingDetails[infoKey],
                    auditDates: {
                        createdAt: invoiceMatter.serviceAgreementBillingDetails.createdAt,
                        lastModifiedAt: invoiceMatter.serviceAgreementBillingDetails.lastModifiedAt,
                    },
                    invoiceMatter,
                    hasAdminFeeAllowed: type === 'billing' && invoiceMatter.hasAdminFeeAllowed,
                    adminFeePercentage: type === 'billing' ? invoiceMatter.adminFeePercentage : undefined,
                })
            ),
        ...(invoice.clientParty[infoKey]
            ? [
                  {
                      key: 'payer',
                      source: 'payer' as const,
                      content: invoice.clientParty[infoKey],
                      auditDates: {
                          createdAt: invoice.clientParty.createdAt,
                          lastModifiedAt: invoice.clientParty.lastModifiedAt,
                      },
                  },
              ]
            : []),
        ...(type === 'billing'
            ? matters
                  .filter(
                      (invoiceMatter) =>
                          invoiceMatter.clientParty.id !== invoice.clientParty.id && invoiceMatter.clientParty[infoKey]
                  )
                  .sort(
                      (a, b) =>
                          Date.parse(b.clientParty.lastModifiedAt ?? '0') -
                          Date.parse(a.clientParty.lastModifiedAt ?? '0')
                  )
                  .map(
                      (invoiceMatter): BillingInfoEntry => ({
                          key: `${invoiceMatter.matterId}-clientPartyBillingDetails`,
                          source: 'client',
                          content: invoiceMatter.clientParty[infoKey],
                          auditDates: {
                              createdAt: invoiceMatter.clientParty.createdAt,
                              lastModifiedAt: invoiceMatter.clientParty.lastModifiedAt,
                          },
                          invoiceMatter,
                      })
                  )
            : []),
    ];
}

/** What each source's note is called. Classic titles a case's note with the case's name instead. */
export const BILLING_INFO_TITLE_IDS: Record<BillingInfoSource, Record<BillingInfoType, string>> = {
    payer: {
        billing: 'invoices.billing-info.payer-billing-notes',
        invoicing: 'invoices.invoicing-info-internal.client-invoicing-info',
    },
    client: {
        billing: 'invoices.billing-info.client-billing-notes',
        invoicing: 'invoices.invoicing-info-internal.client-invoicing-info',
    },
    case: {
        billing: 'invoices.billing-info.case-billing-notes',
        invoicing: 'invoices.invoicing-info-internal.case-invoicing-info',
    },
    serviceAgreement: {
        billing: 'invoices.billing-info.service-agreement-billing-notes',
        invoicing: 'invoices.invoicing-info-internal.service-agreement-invoicing-info',
    },
};

/** Who an entry's note is on, as a link: the payer, a case's client, the case, or its service agreement; null without a name. */
export function billingInfoOwner(invoice: InvoiceData, entry: BillingInfoEntry): ReactNode {
    const party = entry.source === 'payer' ? invoice.clientParty : entry.invoiceMatter?.clientParty;
    if (entry.source === 'case') {
        return entry.invoiceMatter ? (
            <RecordLink
                to={pathForResult('MATTER', entry.invoiceMatter.matterId)}
                label={entry.invoiceMatter.fullName}
            />
        ) : null;
    }
    if (entry.source === 'serviceAgreement') {
        return entry.invoiceMatter?.contractId ? (
            <RecordLink
                to={`/contract/${entry.invoiceMatter.contractId}`}
                className="text-link w-fit no-underline hover:underline"
                target="_blank"
                label={entry.invoiceMatter.contractName ?? entry.invoiceMatter.matterNumber}
            />
        ) : null;
    }
    return party?.officialName ? <PartyName party={party} isLink useOfficialName variant="readonly-other" /> : null;
}

export const BillingInvoicingInfoAccordions = ({ invoice }: Props) => {
    const isInvoicingInfoEnabled = useIsFeatureEnabled(FeatureFlag.INVOICING_INFO_ENABLED);

    const getContent = useCallback(
        (type: BillingInfoType) => {
            const infoKey = type === 'billing' ? 'billingNotes' : 'invoicingInfo';

            const hasContent =
                invoice.clientParty[infoKey] ||
                invoice.invoiceMatters?.find((invoiceMatter) => invoiceMatter.matterBillingDetails[infoKey]) ||
                invoice.invoiceMatters?.find((invoiceMatter) => invoiceMatter.clientParty[infoKey]) ||
                invoice.invoiceMatters?.find(
                    (invoiceMatter) =>
                        invoiceMatter.serviceAgreementBillingDetails[infoKey] ||
                        (type === 'billing' &&
                            (invoiceMatter.hasAdminFeeAllowed || invoiceMatter.adminFeePercentage != null))
                );

            if (!hasContent) {
                return null;
            }

            return (
                <>
                    {getBillingInfoEntries(invoice, type).map((entry) => (
                        <BillingInfoAccordionContent
                            key={entry.key}
                            title={
                                entry.source === 'case'
                                    ? (entry.invoiceMatter?.fullName ?? '')
                                    : t(BILLING_INFO_TITLE_IDS[entry.source][type])
                            }
                            subTitle={entry.source === 'case' ? undefined : billingInfoOwner(invoice, entry)}
                            content={entry.content as string}
                            auditDates={entry.auditDates}
                            hasAdminFeeAllowed={entry.hasAdminFeeAllowed}
                            adminFeePercentage={entry.adminFeePercentage}
                        />
                    ))}
                </>
            );
        },
        [invoice]
    );

    const billingInfoContent = useMemo(() => getContent('billing'), [getContent]);

    const invoicingInfoContent = useMemo(() => {
        return isInvoicingInfoEnabled ? getContent('invoicing') : null;
    }, [getContent, isInvoicingInfoEnabled]);

    // Auto-expand on the falsy→truthy transition of content availability. Boolean-keyed prev-value
    // compare (https://react.dev/reference/react/useState#storing-information-from-previous-renders)
    // so that re-rendered JSX references (which change on every parent render) don't reopen a section
    // the user has collapsed. `expanded` is seeded from the initial content presence to preserve the
    // original effect's on-mount auto-expand when invoice data is already cached.
    const hasBillingInfoContent = Boolean(billingInfoContent);
    const [billingExpanded, setBillingExpanded] = useState(hasBillingInfoContent);
    const [prevHasBillingInfoContent, setPrevHasBillingInfoContent] = useState(hasBillingInfoContent);
    if (hasBillingInfoContent !== prevHasBillingInfoContent) {
        setPrevHasBillingInfoContent(hasBillingInfoContent);
        if (hasBillingInfoContent) {
            setBillingExpanded(true);
        }
    }

    const hasInvoicingInfoContent = Boolean(invoicingInfoContent);
    const [invoicingExpanded, setInvoicingExpanded] = useState(hasInvoicingInfoContent);
    const [prevHasInvoicingInfoContent, setPrevHasInvoicingInfoContent] = useState(hasInvoicingInfoContent);
    if (hasInvoicingInfoContent !== prevHasInvoicingInfoContent) {
        setPrevHasInvoicingInfoContent(hasInvoicingInfoContent);
        if (hasInvoicingInfoContent) {
            setInvoicingExpanded(true);
        }
    }

    return (
        <div>
            <InfoSection titleId="invoices.billing-info.title" open={billingExpanded} onOpenChange={setBillingExpanded}>
                {billingInfoContent}
            </InfoSection>
            {isInvoicingInfoEnabled ? (
                <InfoSection
                    titleId="invoices.invoicing-info-internal.title"
                    open={invoicingExpanded}
                    onOpenChange={setInvoicingExpanded}
                >
                    {invoicingInfoContent}
                </InfoSection>
            ) : null}
        </div>
    );
};

/**
 * A full-width section of the side panel, disabled while it has nothing to show. Its body stays
 * mounted while closed and animates its height.
 */
const InfoSection = ({
    titleId,
    open,
    onOpenChange,
    children,
}: {
    titleId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: ReactNode;
}) => (
    <Collapsible
        open={open}
        onOpenChange={onOpenChange}
        disabled={!children}
        className="bg-paper border-divider text-fg data-disabled:text-fg/50 border-y font-semibold not-first:border-t-0 data-disabled:bg-transparent"
    >
        <CollapsibleTrigger
            className={cn(
                SECTION_HEADER,
                'group focus-visible:ring-ring/50 w-full cursor-pointer text-left outline-none focus-visible:ring-3 data-disabled:cursor-default data-disabled:opacity-[0.38]'
            )}
        >
            <span className="my-2.5 ml-2 flex flex-1">
                <FormattedMessage id={titleId} />
            </span>
            <ExpandMoreIcon className="text-grey-500 transition-transform group-data-[panel-open]:rotate-90" />
        </CollapsibleTrigger>
        <CollapsibleContent keepMounted className={SECTION_PANEL}>
            <div className="border-divider overflow-auto border-t">{children}</div>
        </CollapsibleContent>
    </Collapsible>
);

interface BillingInfoAccordionContentProps {
    title: string;
    content: string;
    auditDates: AuditDates;
    subTitle?: ReactNode;
    hasAdminFeeAllowed?: boolean;
    adminFeePercentage?: number;
}

const BillingInfoAccordionContent = ({
    title,
    content,
    auditDates,
    subTitle,
    hasAdminFeeAllowed,
    adminFeePercentage,
}: BillingInfoAccordionContentProps) => {
    return (
        <div className="border-divider overflow-auto p-4 not-first:border-t">
            <div className="flex flex-col gap-1">
                <p className="text-body1 font-normal">{title}</p>
                {subTitle ? <div className="font-normal">{subTitle}</div> : null}
                {hasAdminFeeAllowed ? (
                    <p className="text-body1 font-bold">
                        <FormattedMessage id="invoices.billing-info.admin-fee-allowed" />
                    </p>
                ) : null}
                {adminFeePercentage != null ? (
                    <p className="text-body1 font-bold">
                        <FormattedMessage
                            id="invoices.billing-info.admin-fee"
                            values={{ percentage: adminFeePercentage }}
                        />
                    </p>
                ) : null}
                <p className="text-body1 font-semibold wrap-anywhere whitespace-pre-line">{content}</p>
                <p className="text-fg-secondary text-[12px] leading-5 font-normal">
                    <FormattedDate value={auditDates.lastModifiedAt || auditDates.createdAt} fallback="" />
                </p>
            </div>
        </div>
    );
};
